import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { depositSubmit } from 'helpers/depositSubmit';

import {
  btag,
  constants,
  IntlManager,
  isUserActionNeedPhotoVerification,
  needsOnlyBankAccounts,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  pushToDataLayer,
  SiteConfigManager,
  useApplicationState,
  useDepositFlow,
  useTranslation,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import {
  DepositChip,
  DepositPopup,
  Icon,
  Label,
  Loader,
  MoneyInput,
  PaymentMethodsBlock,
} from '@atlas/ui';

import { showAlternativeMethodsOrError } from 'common/Notifications/Popups/alternativePaymentUtils';
import { MULTIPLIER } from 'common/Notifications/Popups/OneStepDepositPopup';
import PaymentDetails from 'common/Notifications/Popups/OneStepDepositPopup/components/PaymentDetails';
import PaymentMethodInfo from 'common/Notifications/Popups/OneStepDepositPopup/components/PaymentMethodInfo';
import { VerificationBlock } from 'common/Notifications/Popups/OneStepDepositPopup/components/VereficationBlock';
import { formatInputValue } from 'common/Notifications/Popups/OneStepDepositPopup/utils/utils';
import { useSelfRegulationContext } from 'components/SelfRegulationProvider';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import useFormatter from 'hooks/useFormatter';
import { useDepositPostHog } from 'hooks/usePostHog';

export const OneStepDepositDetails = ({ id }) => {
  const translate = useTranslation();
  const { account, bankAccounts, wallet } = useApplicationState();
  const { currencyFormatter } = useFormatter();
  const [error, setError] = useState(null);
  const isAccountBlocked = useAccountBlocked();
  const [isInputActive, setIsInputActive] = useState(false);
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const { handleDepositStart, handleDepositSuccess } = useDepositPostHog();
  const {
    showSelfRegulationPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopup,
    showSpentLimitPopupIfNeeded,
  } = useSelfRegulationContext();
  const rawChipsSize =
    SiteConfigManager.getConfig('config.config.accountProfile.depositPresetSize') || 's';
  const showCurrencyPresets = SiteConfigManager.getConfig(
    'config.config.accountProfile.showCurrencyPresets',
  );
  const showDecimalsPresets = SiteConfigManager.getConfig(
    'config.config.accountProfile.showDecimalsPresets',
  );
  const depositAmountTemplate = SiteConfigManager.getConfig(
    'config.config.accountProfile.depositAmountTemplate',
  );
  const isHidePaymentMethods = SiteConfigManager.getConfig(
    'config.config.payments.hidePaymentMethod',
  );
  const isShowMaxButton = SiteConfigManager.getConfig('config.config.payments.showMaxAmountButton');

  const { payment } = clientProfileRoutes;
  const { deposit: depositIndex } = paymentRoutes;
  const currency = wallet?.currency;
  const flowStateRef = useRef({ amount: null, selectedMethod: null });
  const loadingNotificationId = useRef();

  const chipSize = rawChipsSize === 'm' ? 'medium' : 'small';
  const requiresProviderHandoff = ({ paymentFormName, processingType, redirectUrl } = {}) =>
    Boolean(redirectUrl) ||
    Boolean(paymentFormName) ||
    processingType === constants.PAYMENT_PROCESSING.REDIRECT ||
    processingType === constants.PAYMENT_PROCESSING.FORM ||
    processingType === 'POPUP';
  const getChipAmount = value => {
    if (!translation) {
      return '';
    }

    return currencyFormatter.format(value * MULTIPLIER, {
      minimumFractionDigits: showDecimalsPresets ? 2 : 0,
      style: 'decimal',
    });
  };

  const callbacks = useMemo(
    () => ({
      onLoadingEnd: () => {
        NotificationManager.removeNotification(loadingNotificationId.current);
      },

      onLoadingStart: () => {
        const currentMethod = flowStateRef.current.selectedMethod;
        const currentAmount = flowStateRef.current.amount;
        loadingNotificationId.current = NotificationManager.display({
          data: {
            headerTitle: 'deposit_loading',
            paymentDetails: {
              paymentMethod: currentMethod?.name,
              value: IntlManager.formatMoney(currentAmount * MULTIPLIER),
            },
            text: 'deposit_loading_text',
            title: 'deposit_loading_title',
          },
          type: 'paymentLoading',
        });
      },
    }),
    [clientProfilePath, depositIndex, payment],
  );

  const {
    amount,
    classifyResult,
    isLoading,
    maxAmount,
    minAmount,
    paymentMethods,
    selectMethod,
    selectedMethod,
    setAmount,
    shouldOfferAlternativeMethods,
    submit,
    updatePaymentMethods,
    validateAmount,
  } = useDepositFlow({
    callbacks,
    currency,
    deviceType: constants.DEVICE_TYPES.DESKTOP,
    operationType: constants.PAYMENT_OPERATION.DEPOSIT,
  });

  const translation = useTranslation();
  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const checkLimits = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({ id: 'accountBlocked', type: 'accountBlocked' });
      return false;
    }

    if (showSpentLimitPopup) {
      await showSpentLimitPopupIfNeeded();
      return false;
    }

    if (showSelfRegulationPopup) {
      await showSelfRegulationPopupIfNeeded();
      return false;
    }

    return true;
  }, [
    isAccountBlocked,
    showSelfRegulationPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopup,
    showSpentLimitPopupIfNeeded,
  ]);

  const depositAmounts = useMemo(() => {
    const depositAmountsByCurrency = depositAmountTemplate[currency];

    if (depositAmountsByCurrency?.length && typeof depositAmountsByCurrency[0] !== 'object') {
      return (depositAmountsByCurrency || []).map(depositAmount => ({
        isDefault: false,
        value: depositAmount,
      }));
    }

    return depositAmountsByCurrency;
  }, [currency, depositAmountTemplate]);

  const getFormattedValue = value => {
    const amountWithMultiplier = value * MULTIPLIER;
    if (!translation) {
      return '';
    }

    return currencyFormatter.format(amountWithMultiplier, {
      minimumFractionDigits: showDecimalsPresets ? 2 : 0,
      style: showCurrencyPresets ? 'currency' : 'decimal',
    });
  };

  const chipCurrency = showCurrencyPresets ? currency : undefined;

  const getActiveItem = (value, currValue) =>
    getFormattedValue(value) === getFormattedValue(currValue);

  const setFirstPaymentMethod = () => {
    if (paymentMethods?.length) selectMethod(paymentMethods?.[0]);
  };

  const handleValueChange = useCallback(
    value => {
      setAmount(value / MULTIPLIER);
    },
    [setAmount],
  );

  const handleBlur = useCallback(() => {
    setIsInputActive(false);

    if (!selectedMethod || amount === '' || amount == null) return;

    const numValue = Number(amount);

    if (Number.isNaN(numValue)) return;

    const amountInCents = numValue * MULTIPLIER;

    if (maxAmount && amountInCents > maxAmount) {
      setAmount(maxAmount / MULTIPLIER);
    } else if (minAmount && amountInCents < minAmount) {
      setAmount(minAmount / MULTIPLIER);
    }
  }, [selectedMethod, amount, minAmount, maxAmount, setAmount]);

  const showErrorPopup = useCallback(text => {
    NotificationManager.display({
      data: {
        headerTitle: 'deposit',
        text: text || 'deposit_failed_message',
        title: 'deposit_failed_title',
        withHeader: false,
      },
      type: 'error',
    });
  }, []);

  const showSuccessPopup = useCallback(({ methodName, submittedAmount, transactionId }) => {
    NotificationManager.display({
      data: {
        headerTitle: 'deposit',
        paymentDetails: {
          paymentMethod: methodName,
          value: IntlManager.formatMoney(submittedAmount * MULTIPLIER),
        },
        text: 'deposit_success_message',
        title: 'deposit_success_title',
        transactionId,
        withHeader: false,
      },
      type: 'success',
    });
  }, []);

  const onSubmit = async () => {
    if (!selectedMethod) return;

    const passesLimits = await checkLimits();
    if (!passesLimits) return;

    const validation = validateAmount(amount * MULTIPLIER);
    if (validation && validation.error) {
      setError({ message: validation.error });
      return;
    }

    const submittedAmount = amount * MULTIPLIER;
    const methodName = selectedMethod?.name;

    handleDepositStart?.({
      amount: amount,
      payment_method: methodName ?? null,
    });

    const paymentOptions = {
      amount: submittedAmount,
      currency,
      pmId: selectedMethod.id,
    };

    const handleDepositError = errorMessage => {
      if (shouldOfferAlternativeMethods) {
        showAlternativeMethodsOrError({
          onSelectMethod: selectMethod,
          onViewAll: updatePaymentMethods,
          operationType: constants.PAYMENT_OPERATION.DEPOSIT,
          pmId: selectedMethod.id,
        });
      } else {
        showErrorPopup(errorMessage);
      }
    };

    try {
      const response = await submit(paymentOptions);

      if (response?.atlasErrorCode) {
        handleDepositError(response.comment || 'deposit_failed_message');
        return;
      }

      const transactionId = response?.publicPaymentId || response?.paymentId;

      pushToDataLayer(
        'deposit_created',
        {
          amount: submittedAmount,
          client: { id: account.id },
          created_at: Date.now(),
          currency,
          payment_method: response?.paymentName ?? methodName,
        },
        btag,
      );

      handleDepositSuccess?.({ transaction_id: transactionId ?? null });

      if (requiresProviderHandoff(response)) {
        depositSubmit({
          amount: submittedAmount,
          data: response,
          paymentMethod: selectedMethod,
          paymentOptions,
          setRedirectUrl: () => {},
        });
      } else {
        showSuccessPopup({ methodName, submittedAmount, transactionId });
      }

      updatePaymentMethods?.();
      handleClose();
    } catch (submitError) {
      setError(submitError);
      handleDepositError(submitError?.message || 'deposit_failed_message');
    }
  };

  useEffect(() => {
    setFirstPaymentMethod();
  }, [paymentMethods, selectMethod]);

  useEffect(() => {
    if (amount !== undefined && amount !== null && amount !== '') {
      const validation = validateAmount(amount * MULTIPLIER);
      if (validation && validation.error) {
        setError({ message: validation.error });
      } else {
        setError(null);
      }
    } else {
      setError(null);
    }
  }, [amount, selectedMethod, validateAmount]);

  useEffect(() => {
    if (!selectedMethod || amount === '' || amount == null) return;

    const numValue = Number(amount);
    if (Number.isNaN(numValue)) return;

    const actualMin = minAmount ? minAmount / MULTIPLIER : 0;
    const actualMax = maxAmount ? maxAmount / MULTIPLIER : 0;

    if (actualMax && numValue > actualMax) {
      setAmount(actualMax);
    } else if (actualMin && numValue < actualMin) {
      setAmount(actualMin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod?.id, minAmount, maxAmount]);

  useEffect(() => {
    flowStateRef.current = { amount, selectedMethod };
  }, [amount, selectedMethod]);

  useEffect(() => {
    const defaultChip = (depositAmounts || []).find(chip => chip?.isDefault);

    if (defaultChip) {
      setAmount(Number(defaultChip?.value));
    }
  }, [depositAmounts, setAmount]);

  const handleMaxClick = useCallback(() => {
    if (maxAmount) {
      setAmount(maxAmount / MULTIPLIER);
    }
  }, [maxAmount, setAmount]);

  const amountFieldBlock = (
    <div className="one-step-deposit__amount-wrapper">
      <MoneyInput
        active={isInputActive}
        currency={currency}
        error={error}
        label="amount"
        onBlur={handleBlur}
        onChange={handleValueChange}
        onFocus={() => setIsInputActive(true)}
        value={formatInputValue(amount)}
      />
      {isShowMaxButton && (
        <span className="one-step-deposit__amount-max" onClick={handleMaxClick}>
          <Label message="max_key" />
        </span>
      )}
    </div>
  );

  const validationErrorBlock = error && (
    <div className="one-step-deposit-form-error">
      <Icon className="error-icon" height={18} name="error" width={18} />
      <Label className="error-message" message={error.message} />
    </div>
  );

  const chipsBlock = (
    <div className={cx('one-step-deposit__chips', `one-step-deposit__chips--${chipSize}`)}>
      {(depositAmounts || []).map(({ isHighlighted, value }) => (
        <DepositChip
          active={getActiveItem(value, amount)}
          amount={getChipAmount(value)}
          currency={chipCurrency}
          highlighted={isHighlighted}
          key={value}
          onClick={() => setAmount(value)}
          size={chipSize}
        />
      ))}
    </div>
  );

  const showVerificationMessage = useMemo(() => {
    return (
      !(
        needsOnlyBankAccounts(account, bankAccounts) &&
        selectedMethod &&
        selectedMethod.autofillProhibited
      ) &&
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.DEPOSIT)
    );
  }, [account, bankAccounts, selectedMethod]);

  const infoTextKey = `pmid_${selectedMethod?.id}_deposit`;
  const isDepositDisabled = isLoading || !!error || !amount;

  return (
    <DepositPopup
      footerContent={<PaymentDetails selectedMethod={selectedMethod} />}
      isDepositDisabled={isDepositDisabled}
      isDepositLoading={isLoading}
      onClose={handleClose}
      onDeposit={onSubmit}
      showVerification={showVerificationMessage}
      verificationContent={<VerificationBlock onClose={handleClose} />}
    >
      {isLoading ? (
        <div className="one-step-deposit__loader">
          <Loader />
        </div>
      ) : (
        <>
          {translate[infoTextKey] && (
            <Label className="one-step-deposit__info-text" message={infoTextKey} />
          )}
          <PaymentMethodsBlock
            className={'one-step-deposit__payment-block'}
            currentMaxAmount={maxAmount}
            currentMinAmount={minAmount}
            currentPaymentMethodInfoText={<PaymentMethodInfo selectedMethod={selectedMethod} />}
            hidePaymentMethods={isHidePaymentMethods}
            onSelect={selectMethod}
            paymentMethods={paymentMethods}
          />
          <div className={'one-step-deposit__amount'}>
            {amountFieldBlock}
            {validationErrorBlock}
          </div>
          {chipsBlock}
        </>
      )}
    </DepositPopup>
  );
};
